/**
 * 採用データ(現行シート「CC採用データ」の CSV)の取込(D-197)。
 * ファイルを選ぶ → 照合の結果(取り込む人・見つからない人)を確認 → 「取り込む」。
 * 7/31 までの日別の数字と稼働日だけを training_tracks に入れる(日次集計・PL は変わらない)。
 */
import { useState } from 'react';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { parseCsv, readCsvFile } from '@core/lib/csv';
import { todayStr, toDateLabel } from '@core/lib/date';
import { BUSINESS_LABELS, type Operator } from '@core/lib/types';
import { formatCount, formatHours } from '@core/utils/formatters';
import type { TrainingTrackDoc } from './training';
import { SHEET_UNTIL } from './training';
import { parseRecruitSheet, planSheetImport, type SheetImportPlan } from './trainingSheet';
import { importSheetEntries } from './trainingRepo';

export default function TrainingImportModal({ operators, existing, userId, onClose, onDone }: { operators: Operator[]; existing: TrainingTrackDoc[]; userId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [plan, setPlan] = useState<SheetImportPlan | null>(null);
  const [range, setRange] = useState<{ from: string | null; to: string | null } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function pick(file: File | undefined) {
    setPlan(null);
    setError('');
    if (!file) return;
    setFileName(file.name);
    try {
      const table = parseCsv(await readCsvFile(file));
      const parsed = parseRecruitSheet([table.headers, ...table.rows], todayStr());
      setRange({ from: parsed.dateFrom, to: parsed.dateTo });
      setPlan(planSheetImport(parsed.rows, operators));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ファイルを読めませんでした');
    }
  }

  async function run() {
    if (!plan) return;
    setBusy(true);
    try {
      const n = await importSheetEntries(plan.entries, existing, fileName, userId);
      toast(`採用データを取り込みました(${formatCount(n)}件)`);
      onDone();
    } catch (e) {
      toast(e instanceof Error ? `取り込めませんでした: ${e.message}` : '取り込めませんでした');
      setBusy(false);
    }
  }

  const manual = new Set(existing.filter((d) => d.start_source === 'manual').map((d) => d.id));

  return (
    <Modal onClose={onClose} width="min(860px, 94vw)">
      <h3 className="training-modal-title">採用データを取り込む</h3>
      <p className="ui-hint">現行シート「CC採用データ」を CSV で書き出したファイルを選んでください。{toDateLabel(SHEET_UNTIL)} より前の日別の数字と稼働日を取り込みます(PL・日次集計は変わりません)。</p>
      <div className="training-actions">
        <input type="file" accept=".csv,text/csv" aria-label="採用データのCSV" onChange={(e) => void pick(e.target.files?.[0])} />
      </div>
      {error && <p className="training-error">{error}</p>}
      {plan && (
        <>
          <p>
            取り込む: <b>{formatCount(plan.entries.length)}件</b>(人 × カテゴリ)
            {range?.from && ` ・ シートの日付 ${toDateLabel(range.from as never)} 〜 ${toDateLabel(range.to as never)}`}
            {plan.unmatched.length > 0 && <> ・ マスタに見つからない: <b>{formatCount(plan.unmatched.length)}件</b></>}
          </p>
          <div className="report-scroll is-auto training-preview">
            <table className="report-table no-stack">
              <caption className="sr-only">取り込む内容</caption>
              <thead>
                <tr>
                  <th scope="col">シートの名前</th>
                  <th scope="col">マスタ</th>
                  <th scope="col">カテゴリ</th>
                  <th scope="col">稼働日</th>
                  <th scope="col" className="is-num">日数</th>
                  <th scope="col" className="is-num">時間</th>
                  <th scope="col" className="is-num">取得</th>
                </tr>
              </thead>
              <tbody>
                {plan.entries.map((e) => (
                  <tr key={e.key}>
                    <td>{e.sheetName}</td>
                    <td>
                      {e.opCode} {e.operatorName}
                    </td>
                    <td>{BUSINESS_LABELS[e.business]}</td>
                    <td>
                      {toDateLabel(e.startDate)}
                      {manual.has(e.key) && <span className="ui-hint">(指定済みの日付を使う)</span>}
                    </td>
                    <td className="is-num">{formatCount(e.dayCount)}</td>
                    <td className="is-num">{formatHours(e.hours)}</td>
                    <td className="is-num">{formatCount(e.appointments)}</td>
                  </tr>
                ))}
                {plan.unmatched.map((u, i) => (
                  <tr key={`u${i}`} className="is-muted">
                    <td>{u.name}</td>
                    <td colSpan={6}>
                      取り込みません({BUSINESS_LABELS[u.business]}・{u.reason})
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      <div className="training-actions">
        <button type="button" className="ui-btn ui-btn-primary" onClick={() => void run()} disabled={busy || !plan || plan.entries.length === 0}>
          {busy ? '取り込み中…' : '取り込む'}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose} disabled={busy}>
          やめる
        </button>
      </div>
    </Modal>
  );
}
